import React, { useState, useEffect, useCallback } from "react";
import "./VisitorBandwidth.css";
import myAxios from "../AxiosUrl";
import Loading from "../Loading/Loading";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const VisitorBandwidth = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedPeriod]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchAnalyticsData();
      }, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, selectedPeriod]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching analytics for period:", selectedPeriod);

      // Remove "all" period logic and always pass the period parameter
      const url = `/visitors/analytics?period=${selectedPeriod}`;
      const response = await myAxios.get(url);

      if (response.data.success) {
        setAnalyticsData(response.data.data);
        console.log("Analytics data fetched successfully:", response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch analytics data");
      }
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      setError(
        error.response?.data?.message || "Error fetching analytics data"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalyticsData();
    setRefreshing(false);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const formatBytes = (bytes) => {
    // Handle undefined, null, or NaN values
    if (bytes === undefined || bytes === null || isNaN(bytes)) {
      return "0 Bytes";
    }

    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatBytesWithSpeed = (bytes) => {
    const formatted = formatBytes(bytes);
    return formatted;
  };

  const getPeriodDisplayName = (period) => {
    switch (period) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "week":
        return "This Week";
      case "month":
        return "This Month";
      case "last_month":
        return "Last Month";
      default:
        return period;
    }
  };

  const renderPeriodData = () => {
    console.log("Rendering period data:", {
      selectedPeriod,
      analyticsData,
    }); // Debug log

    // Safety function to ensure we get a number for visitor counts
    const getVisitorCount = (data) => {
      if (typeof data === "number") return data;
      if (typeof data === "object" && data !== null) {
        if (data.visitorCount !== undefined) return data.visitorCount;
        if (data.count !== undefined) return data.count;
      }
      return 0;
    };

    // Safety function to ensure we get a number for bandwidth
    const getBandwidth = (data) => {
      if (typeof data === "number") return data;
      if (typeof data === "object" && data !== null) {
        if (data.bandwidth !== undefined) return data.bandwidth;
      }
      return 0;
    };

    // Show individual period data
    const bandwidth = getBandwidth(analyticsData);
    const visitorCount = getVisitorCount(analyticsData);

    return (
      <div className="bandwidth-cards">
        <div className="bandwidth-card single-period">
          <div className="bandwidth-icon">
            <span className="material-icons">
              {selectedPeriod === "today"
                ? "today"
                : selectedPeriod === "yesterday"
                ? "yesterday"
                : selectedPeriod === "week"
                ? "date_range"
                : selectedPeriod === "month"
                ? "calendar_month"
                : selectedPeriod === "last_month"
                ? "event"
                : "analytics"}
            </span>
          </div>
          <div className="bandwidth-content">
            <div className="bandwidth-number">
              {formatBytesWithSpeed(bandwidth)}
            </div>
            <div className="bandwidth-label">
              {getPeriodDisplayName(selectedPeriod)} Usage
            </div>
            <div className="visitor-count">{visitorCount} visitors</div>
          </div>
        </div>
      </div>
    );
  };

  const renderChart = () => {
    if (!analyticsData) return null;

    // Safety function to ensure we get a number for visitor counts
    const getVisitorCount = (data) => {
      if (typeof data === "number") return data;
      if (typeof data === "object" && data !== null) {
        if (data.visitorCount !== undefined) return data.visitorCount;
        if (data.count !== undefined) return data.count;
      }
      return 0;
    };

    // Safety function to ensure we get a number for bandwidth
    const getBandwidth = (data) => {
      if (typeof data === "number") return data;
      if (typeof data === "object" && data !== null) {
        if (data.bandwidth !== undefined) return data.bandwidth;
      }
      return 0;
    };

    // Show individual period data in a simple format
    const bandwidth = getBandwidth(analyticsData);
    const visitorCount = getVisitorCount(analyticsData);

    const chartData = {
      labels: [getPeriodDisplayName(selectedPeriod)],
      datasets: [
        {
          label: "Bandwidth Usage (MB)",
          data: [bandwidth / (1024 * 1024)], // Convert to MB
          backgroundColor: "rgba(99, 102, 241, 0.7)",
          borderColor: "rgba(99, 102, 241, 1)",
          borderWidth: 1,
          yAxisID: "y",
        },
        {
          label: "Visitor Count",
          data: [visitorCount],
          backgroundColor: "rgba(168, 85, 247, 0.7)",
          borderColor: "rgba(168, 85, 247, 1)",
          borderWidth: 1,
          yAxisID: "y1",
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        title: {
          display: true,
          text: `${getPeriodDisplayName(selectedPeriod)} Analytics Overview`,
          font: {
            size: 16,
            weight: "bold",
          },
        },
        legend: {
          display: true,
          position: "top",
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: "Period",
          },
        },
        y: {
          type: "linear",
          display: true,
          position: "left",
          title: {
            display: true,
            text: "Bandwidth (MB)",
          },
        },
        y1: {
          type: "linear",
          display: true,
          position: "right",
          title: {
            display: true,
            text: "Visitor Count",
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    };

    return (
      <div className="chart-container">
        <div
          style={{
            height:
              window.innerWidth >= 768
                ? "400px"
                : window.innerWidth >= 480
                ? "300px"
                : "250px",
          }}
        >
          <Bar data={chartData} options={options} />
        </div>
      </div>
    );
  };

  return (
    <div className="bandwidth-container">
      <div className="bandwidth-header">
        <h1>Visitor Analytics & Bandwidth Usage</h1>
        <div className="bandwidth-actions">
          <button
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh (30s)
          </label>
        </div>
      </div>

      {/* Period Selection */}
      <div className="bandwidth-period-selector">
        <div className="period-buttons">
          {["today", "yesterday", "week", "month", "last_month"].map(
            (period) => (
              <button
                key={period}
                className={`period-btn ${
                  selectedPeriod === period ? "active" : ""
                }`}
                onClick={() => handlePeriodChange(period)}
              >
                {getPeriodDisplayName(period)}
              </button>
            )
          )}
        </div>
      </div>

      {/* Bandwidth Data */}
      <div className="bandwidth-content">
        {loading ? (
          <Loading />
        ) : error ? (
          <div className="error-container">
            <span className="material-icons">error</span>
            <p>{error}</p>
          </div>
        ) : analyticsData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderPeriodData()}
          </motion.div>
        ) : (
          <div className="no-data-container">
            <span className="material-icons">info</span>
            <p>No analytics data available</p>
          </div>
        )}
      </div>

      {/* Chart Visualization */}
      {analyticsData && !loading && !error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="chart-section"
        >
          {renderChart()}
        </motion.div>
      )}
    </div>
  );
};

export default VisitorBandwidth;
